import { useLogger } from '#layers/base/composables/useLogger'
import type { CustomerReferenceTag } from '~/types'
import type { MonthlyItems } from '~/types/order-history'

export interface OrderInformation {
  lastOrderedDate: string | null
  lastOrderedQuantity: number
  reference: CustomerReferenceTag
}

export const useOrderHistoryInfoStore = defineStore('orderHistoryInfo', () => {
  const { baseURL } = useRuntimeConfig().app
  const log = useLogger('useOrderHistoryInfoStore')
  const userStore = useUserStore()
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()

  const accessToken = computed(() => userStore.accessToken)
  const customerHandle = computed(() => userStore.customerHandle)
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const { withAuthRetry } = useWithAuthRetry()

  const products = ref<Record<string, OrderInformation>>({})
  const isInfoLoading = ref(false)
  const isMonthlyLoading = ref(false)

  function buildInfoUrl(productHandle: string) {
    return `/api/intern/order-information/${unref(customerHandle)}/${productHandle}`
  }

  function isCached(productHandle: string, reference: CustomerReferenceTag) {
    return products.value[productHandle]?.reference === reference
  }

  async function fetchOrderInformation(
    productHandle: string,
    reference: CustomerReferenceTag,
  ): Promise<OrderInformation | undefined> {
    if (!unref(customerHandle) || !unref(accessToken)) return
    if (isCached(productHandle, reference)) return products.value[productHandle]

    try {
      isInfoLoading.value = true
      const response = await withAuthRetry(() => $fetch<OrderInformation>(buildInfoUrl(productHandle), {
        baseURL,
        headers: headerFields.value,
        query: { 'monthly-orders': 'false' },
        ...abortIfNotAuthenticated,
        onResponseError({ response }) {
          if (response.status !== 401)
            log.warn('Failed to fetch order information', { productHandle, responseStatus: response.status })
        },
      }))

      products.value[productHandle] = {
        ...response,
        reference: response.reference || reference,
      }
      return products.value[productHandle]
    }
    finally {
      isInfoLoading.value = false
    }
  }

  async function fetchMonthlyOrders(productHandle: string): Promise<MonthlyItems[] | undefined> {
    if (!unref(customerHandle) || !unref(accessToken)) return

    try {
      isMonthlyLoading.value = true
      return await withAuthRetry(() => $fetch<MonthlyItems[]>(buildInfoUrl(productHandle), {
        baseURL,
        headers: headerFields.value,
        query: { 'monthly-orders': 'true' },
        ...abortIfNotAuthenticated,
        onResponseError({ response }) {
          if (response.status !== 401)
            log.warn('Failed to fetch monthly orders', { productHandle, responseStatus: response.status })
        },
      }))
    }
    finally {
      isMonthlyLoading.value = false
    }
  }

  function $reset() {
    products.value = {}
    isInfoLoading.value = false
    isMonthlyLoading.value = false
  }

  return {
    products,
    isInfoLoading,
    isMonthlyLoading,
    fetchOrderInformation,
    fetchMonthlyOrders,
    $reset,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderHistoryInfoStore, import.meta.hot))
}
